// ──────────────────────────── Tickets Statuses ────────────────────────────
// Canonical module status config (plan-business-semantic §2.5.2).
// Single source of truth for ticket status and category.

import type { StatusToken } from "@lib/config/statusTokens";
import {
  Bug,
  CheckCircle2,
  Clock,
  FileText,
  Layers,
  Lightbulb,
  Lock,
  type LucideIcon,
  Settings,
} from "lucide-react";

export interface StatusConfig {
  readonly code: string;
  readonly label: string;
  readonly token: StatusToken;
  readonly icon?: LucideIcon;
}

// ──────────────────────────── Statuts (TicketStatus) ────────────────────────────

export const STATUT_TICKET = {
  open: { code: "open", label: "Ouvert", token: "pending", icon: Clock },
  proposition_solution: {
    code: "proposition_solution",
    label: "Solution proposée",
    token: "awaiting-approval",
    icon: Lightbulb,
  },
  resolved: { code: "resolved", label: "Résolu", token: "completed", icon: CheckCircle2 },
  closed: { code: "closed", label: "Fermé", token: "completed", icon: Lock },
} as const satisfies Record<string, StatusConfig>;

export type StatutTicketCode = keyof typeof STATUT_TICKET;

// ──────────────────────────── Catégories (TicketCategory) ────────────────────────────
// Codes = backend TicketCategory enum (source of truth) — labels FR from the enum.

export const CATEGORIE_TICKET = {
  bug_bloquant: { code: "bug_bloquant", label: "Bug bloquant", token: "rejected", icon: Bug },
  bug_mineur: { code: "bug_mineur", label: "Bug mineur", token: "rejected", icon: Bug },
  interface_mouche: {
    code: "interface_mouche",
    label: "Problème Interface",
    token: "pending",
    icon: Layers,
  },
  fonctionnalite_manquante: {
    code: "fonctionnalite_manquante",
    label: "Fonctionnalité manquante",
    token: "awaiting-approval",
    icon: Lightbulb,
  },
  lenteur: { code: "lenteur", label: "Lenteur", token: "overdue", icon: Clock },
  ergonomie: { code: "ergonomie", label: "Ergonomie", token: "in-progress", icon: Settings },
  documentation: { code: "documentation", label: "Documentation", token: "draft", icon: FileText },
  securite: { code: "securite", label: "Sécurité", token: "blocked", icon: Lock },
} as const satisfies Record<string, StatusConfig>;

export type CategorieTicketCode = keyof typeof CATEGORIE_TICKET;

// ──────────────────────────── Resolution helpers (canonical §2.5.2) ────────────────────────────

export function resolveStatutTicket(statut: string | null | undefined): StatutTicketCode {
  return statut && statut in STATUT_TICKET ? (statut as StatutTicketCode) : "open";
}

export function resolveCategorieTicket(categorie: string | null | undefined): CategorieTicketCode {
  return categorie && categorie in CATEGORIE_TICKET
    ? (categorie as CategorieTicketCode)
    : "bug_mineur";
}

export function getTicketStatusConfig(status: string): StatusConfig {
  return STATUT_TICKET[status as StatutTicketCode] ?? STATUT_TICKET.open;
}

export function getTicketCategoryConfig(category: string): StatusConfig {
  return CATEGORIE_TICKET[category as CategorieTicketCode] ?? CATEGORIE_TICKET.bug_mineur;
}

export function toTicketStatusOptions(
  config: Readonly<Record<string, StatusConfig>>,
): { value: string; label: string }[] {
  return Object.values(config).map(({ code, label }) => ({ value: code, label }));
}
